import { makeAutoObservable } from 'mobx'
import { api } from '@/shared/api/endpoints'
import type {
  AutoWithdrawalResponse, BalancesResponse, ChartResponse, FiatCurrency,
  OverviewResponse, WalletResponse,
} from '@/shared/api/types'
import { Resource } from '@/stores/lib/Resource'
import type { UiStore } from '@/stores/UiStore'

const VIEW_KEY = 'cck-fiat-view'

/** Статусы, в которых заявка на вывод ещё «в обработке». */

/** Периоды графика из макета. */
export const CHART_PERIODS = [
  { value: '7', label: 'За неделю' },
  { value: '30', label: 'За месяц' },
  { value: '90', label: 'За три месяца' },
] as const

/**
 * Данные кабинета: сводка, график, балансы, кошельки, автовыводы.
 *
 * Один стор на всё это, а не четыре, потому что у них общий
 * переключатель фиатной валюты: сменив USD на RUB, надо перезагрузить
 * и сводку, и график, и балансы — разложенные по разным сторам, они
 * рано или поздно разъедутся, и на экране окажутся доллары в одной
 * плитке и рубли в другой.
 */
export class CabinetStore {
  view: FiatCurrency = 'usd'
  chartDays = 7

  readonly overview: Resource<OverviewResponse>
  readonly chart: Resource<ChartResponse>
  readonly balances: Resource<BalancesResponse>
  readonly wallets: Resource<WalletResponse[]>
  readonly rules: Resource<AutoWithdrawalResponse[]>

  /** Сколько заявок на вывод сейчас в обработке — для плитки сводки. */

  private readonly ui: UiStore

  constructor(ui: UiStore) {
    this.ui = ui
    this.view = readView()

    this.overview = new Resource(() => api.account.overview(this.view))
    this.chart = new Resource(() => api.account.incomeChart(this.chartDays, this.view))
    this.balances = new Resource(() => api.account.balances(this.view))
    this.wallets = new Resource(async () => (await api.money.wallets()).items)
    this.rules = new Resource(async () => (await api.money.autoWithdrawals()).items)

    makeAutoObservable<this, 'ui'>(this, { ui: false })
  }

  get fiatSign(): string {
    return this.view === 'usd' ? '$' : '₽'
  }

  /** Загрузка всего разом: экран обзора показывает всё сразу. */
  async loadOverview(): Promise<void> {
    await Promise.all([
      this.overview.load(),
      this.chart.load(),
      this.loadHoldings(),
    ])
  }

  /**
   * Балансы, кошельки и правила — без сводки и графика. Нужно админскому
   * обзору: сводку и график он берёт из админского API, а вот валюты
   * и вывод у него свои, как у любого аккаунта, — там лежит комиссия.
   */
  async loadHoldings(): Promise<void> {
    await Promise.all([
      this.balances.load(),
      this.wallets.load(),
      this.rules.load(),
    ])
  }

  /**
   * Сколько заявок на вывод ещё в полёте.
   *
   * Приходит полем сводки. Раньше кабинет считал это число сам —
   * пятью запросами `withdrawals?limit=1&status=…`, по одному на
   * статус, ради поля `total` из каждого. На обзоре получалось
   * одиннадцать запросов вместо шести, а браузер держит к одному
   * хосту шесть соединений: остальные вставали в очередь, и вместе
   * с ними — загрузка шрифта.
   */
  get pendingCount(): number {
    return this.overview.value?.pending_withdrawal_count ?? 0
  }


  setView(view: FiatCurrency): void {
    if (view === this.view) return
    this.view = view
    writeView(view)
    // Перезагружаем всё, что считается в фиате, одновременно — иначе
    // на экране на секунду окажутся доллары рядом с рублями.
    void Promise.all([this.overview.load(), this.chart.load(), this.balances.load()])
  }

  setChartDays(days: number): void {
    if (days === this.chartDays) return
    this.chartDays = days
    void this.chart.load()
  }

  /** Сбросить всё при выходе: чужие балансы не должны мелькнуть. */
  reset(): void {
    this.overview.reset()
    this.chart.reset()
    this.balances.reset()
    this.wallets.reset()
    this.rules.reset()
  }

  notifyFailure(error: Error | null): void {
    if (error) this.ui.failure(error.message)
  }
}

function readView(): FiatCurrency {
  try {
    const value = window.localStorage.getItem(VIEW_KEY)
    return value === 'rub' ? 'rub' : 'usd'
  } catch {
    return 'usd'
  }
}

function writeView(view: FiatCurrency): void {
  try {
    window.localStorage.setItem(VIEW_KEY, view)
  } catch {
    // Не сохранилось — выбор проживёт до перезагрузки.
  }
}
